import { notification } from 'antd';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { generatePath, useNavigate } from 'react-router';

import { PATHS } from '@/shared/config';
import { useAppSelector } from '@/shared/hooks';

import { organizationModel } from '@/entities/organization';

import { BillingProductAccessState } from '../config';

type UseBillingProductAccessGuardParams = {
  accessDeniedDescription: string;
  organizationID: string;
};

export const useBillingProductAccessGuard = (
  params: UseBillingProductAccessGuardParams,
): BillingProductAccessState => {
  const { accessDeniedDescription, organizationID } = params;

  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const organization = useAppSelector(
    organizationModel.selectors.selectOrganization,
  );
  const organizationLoadingState = useAppSelector(
    organizationModel.selectors.selectOrganizationLoading,
  );

  const isAccessDenied =
    organizationLoadingState === 'succeeded' &&
    !organization?.YourPermissions?.BillingCanEditProducts;

  useEffect(() => {
    if (!isAccessDenied) {
      return;
    }

    notification.warning({
      message: formatMessage({
        id: 'global.accessDenied',
        defaultMessage: 'Access denied',
      }),
      description: accessDeniedDescription,
    });

    navigate(generatePath(PATHS.organization, { organizationID }));
  }, [accessDeniedDescription, isAccessDenied, organizationID]);

  if (isAccessDenied) {
    return 'denied';
  }

  if (organizationLoadingState === 'failed') {
    return 'failed';
  }

  if (organizationLoadingState === 'succeeded') {
    return 'allowed';
  }

  return 'loading';
};
